import { FC } from 'react';
import { IconInfoRegular, IconInfoSolid } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { Drawer } from '@common/overlay-manager';
import { Typography } from '@common/styles';
import { BeautyIngredientDetail } from '@domains/pdp-common';

import { useBeautyService } from '../../services/useBeautyService';
import { IngredientNoticeView } from '../popup';
import { IngredientListItem } from './IngredientListItem';

const ALL_INGREDINET = {
  title: (count: number) => `${count}개의 전 성분을 확인해보세요.`,
  colors: {
    primary: vars.v3Color.skyBlue400,
    warning: vars.v3Color.yellow400,
  },
};

interface Props {
  list: BeautyIngredientDetail['ingredient_list'];
}

export const IngredientList: FC<React.PropsWithChildren<Props>> = ({ list }) => {
  const { ingredientNoticeOpen, handleIngredientNoticePopup } = useBeautyService();
  const { title, colors } = ALL_INGREDINET;

  return (
    <Root>
      <Title>
        <p className={Typography.BODY_18_SEMIBOLD}>{title(list.length)}</p>
        <IconInfoSolid className='icon' size={26} v3Color='gray200' onClick={() => handleIngredientNoticePopup(true)} />
      </Title>
      <List>
        {list.map((item) => (
          <IngredientListItem key={item.name} item={item} colors={colors} />
        ))}
      </List>
      <Info>
        <InfoTitle>
          <IconInfoRegular className='icon' size={20} v3Color='gray400' />
          <p className={Typography.BODY_14_MEDIUM}>성분 정보</p>
        </InfoTitle>
        <InfoText className={Typography.BODY_13_MEDIUM}>
          성분 정보는 판매업자가 작성한 전 성분 표기에 기초하여 작성되었으며, 판매업자가 표준화된 성분명으로 기재하지
          않은 등 특이사항이 있는 경우 일부 성분에 대한 정보가 제공되지 않습니다.
          <br />
          식품의약품안전처고시 규정에 따른 알레르기 유발성분, 기능성 화장품 정보와 개별 성분의 주된 배합 목적을 매칭하여
          제공하고 있으며 성분간의 배합, 개인의 피부와 사용 환경 등에 따라 제품의 효능, 효과는 변경될 수 있습니다.
        </InfoText>
      </Info>
      <Drawer height='auto' open={ingredientNoticeOpen} onClose={() => handleIngredientNoticePopup(false)}>
        <IngredientNoticeView />
      </Drawer>
    </Root>
  );
};

const Root = styled.div`
  padding: 20px 16px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;

  .icon {
    margin-left: 4px;
  }
`;

const List = styled.ul`
  padding-bottom: 40px;
`;

const Info = styled.div`
  padding: 16px;
  background: ${vars.v3Color.gray100};
  color: ${vars.v3Color.gray500};
  border-radius: 8px;
`;

const InfoTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  .icon {
    margin-right: 2px;
  }
`;

const InfoText = styled.p`
  line-height: 150%;
`;
